import React, { useEffect, useRef, useState } from "react";
// VW_TO_VH deliberately not imported — canvas uses window.innerWidth/innerHeight instead.

/**
 * Visual layout editor. Each item is rendered as a labeled rectangle on a
 * wireframe canvas at its current position (per-item x/y/w override if
 * present, otherwise the auto-layout slot). Free-form drag (move) and
 * corner-handle resize (width). Apply commits new x/y/w per item.
 *
 * Optional `textTile` shows a draggable + resizable representation of the
 * description block on /works/deeper/:slug — its updated position is
 * returned via the second arg of `onChange`.
 *
 * @param {boolean} open
 * @param {Array<{id: string, url: string, label: string, x?: number, y?: number, w?: number}>} items
 * @param {(count: number) => Array<{x: number, y: number, w: number}>} layoutFn
 * @param {(count: number) => number} pageHeightVhFn
 * @param {{ x: number, y: number, w: number, label: string } | null} textTile
 * @param {string} title
 * @param {(items: Array, textTile: object | null) => void} onChange
 * @param {() => void} onClose
 * @param {number} [aspectRatio] - card height/width ratio in the wireframe (default 0.625, i.e. 16/10)
 */
export default function LayoutModal({
  open,
  items,
  layoutFn,
  pageHeightVhFn,
  textTile,
  title,
  onChange,
  onClose,
  aspectRatio = 0.625,
}) {
  const [draftItems, setDraftItems] = useState(items);
  const [draftText, setDraftText] = useState(textTile);
  const [drag, setDrag] = useState(null); // { kind: "item"|"text", index, mode: "move"|"resize", startX, startY, origX, origY, origW }
  const canvasRef = useRef(null);

  // Measured naturalHeight/naturalWidth ratio per image URL. Falls back to
  // the aspectRatio prop until the image loads. A ref tracks which URLs have
  // already been requested so we don't re-issue the same load on every render.
  const [imgRatios, setImgRatios] = useState({});
  const pendingUrls = useRef(new Set());

  useEffect(() => {
    if (open) {
      setDraftItems(items);
      setDraftText(textTile);
      setDrag(null);
    }
  }, [open, items, textTile]);

  // Load natural image dimensions whenever the modal opens or items change.
  useEffect(() => {
    if (!open) return;
    items.forEach((item) => {
      if (!item.url || pendingUrls.current.has(item.url)) return;
      pendingUrls.current.add(item.url);
      const img = new window.Image();
      img.onload = () => {
        if (img.naturalWidth > 0) {
          setImgRatios((prev) => ({
            ...prev,
            [item.url]: img.naturalHeight / img.naturalWidth,
          }));
        }
      };
      img.src = item.url;
    });
  }, [open, items]);

  if (!open) return null;

  // Use the actual viewport ratio so the canvas preview matches what the page
  // renders. Hardcoding 16/9 caused gaps in the editor but overlaps on the
  // real page whenever the window wasn't exactly 16:9.
  const actualVwToVh = window.innerWidth / window.innerHeight;

  const ratiosList = draftItems.map((item) =>
    item.url && imgRatios[item.url] !== undefined ? imgRatios[item.url] : aspectRatio,
  );
  const auto = layoutFn(draftItems.length, undefined, ratiosList, actualVwToVh);
  // Also account for items already saved at large y values — pageHeightVhFn
  // only knows about auto-layout positions, not manual overrides.
  const savedMaxBottom = draftItems.reduce((max, item, i) => {
    if (typeof item.y !== "number" || typeof item.w !== "number") return max;
    const ratio = ratiosList[i] ?? aspectRatio;
    return Math.max(max, item.y + item.w * actualVwToVh * ratio);
  }, 0);
  const heightVh = Math.max(pageHeightVhFn(draftItems.length, actualVwToVh), savedMaxBottom + 20);

  const CANVAS_W = 380;
  // 1vh in canvas px: derived from actual viewport ratio so tiles rendered at
  // their measured aspect ratio align with the y-positions from layoutFn.
  const VH_TO_PX = (CANVAS_W / 100) / actualVwToVh;
  const CANVAS_H = Math.max(280, heightVh * VH_TO_PX);
  const vhToPx = VH_TO_PX;
  const pctToPx = CANVAS_W / 100;

  const xToPx = (x) => x * pctToPx;
  const yToPx = (y) => y * vhToPx;
  const wToPx = (w) => w * pctToPx; // vw treated as % of canvas width

  // Per-image ratio from measurement; falls back to aspectRatio prop.
  const urlRatio = (url) => (url && imgRatios[url] !== undefined ? imgRatios[url] : aspectRatio);
  // Height of an image tile in canvas px given its width (vw%) and URL.
  const cardHPx = (w, url) => wToPx(w) * urlRatio(url);
  // Height of the text tile uses the prop default (no image to measure).
  const textHPx = (w) => Math.max(28, wToPx(w) * aspectRatio * 0.55);

  const pxToX = (px) => Math.max(0, Math.min(100, px / pctToPx));
  const pxToY = (px) => Math.max(0, Math.min(heightVh, px / vhToPx));
  const pxToW = (px) => Math.max(2, Math.min(100, px / pctToPx));

  // Edge-aware clamps: tile must stay inside page bounds.
  const clampX = (x, w) => Math.max(0, Math.min(100 - w, x));
  const clampY = (y, w, kind, url) => {
    const ratio = kind === "text" ? aspectRatio * 0.55 : urlRatio(url);
    return Math.max(0, Math.min(heightVh - w * ratio, y));
  };

  const effectiveItem = (item, i) => {
    const slot = auto[i] || { x: 6, y: 6, w: 31 };
    return {
      x: typeof item.x === "number" ? item.x : slot.x,
      y: typeof item.y === "number" ? item.y : slot.y,
      w: typeof item.w === "number" ? item.w : slot.w,
    };
  };

  const onMouseDownItem = (i, mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const eff = effectiveItem(draftItems[i], i);
    setDrag({
      kind: "item",
      index: i,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origX: eff.x,
      origY: eff.y,
      origW: eff.w,
    });
  };

  const onMouseDownText = (mode) => (e) => {
    if (!draftText) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      kind: "text",
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origX: draftText.x,
      origY: draftText.y,
      origW: draftText.w,
    });
  };

  const onCanvasMove = (e) => {
    if (!drag) return;
    const dxPx = e.clientX - drag.startX;
    const dyPx = e.clientY - drag.startY;
    if (drag.kind === "item") {
      const i = drag.index;
      setDraftItems((prev) =>
        prev.map((it, idx) => {
          if (idx !== i) return it;
          const eff = effectiveItem(it, i);
          if (drag.mode === "move") {
            const rawX = pxToX(xToPx(drag.origX) + dxPx);
            const rawY = pxToY(yToPx(drag.origY) + dyPx);
            return {
              ...it,
              x: clampX(rawX, eff.w),
              y: clampY(rawY, eff.w, "item", it.url),
            };
          }
          // resize: dx in canvas px increases width; clamp so tile's right
          // edge stays at-or-inside the page right edge
          const rawW = pxToW(wToPx(drag.origW) + dxPx);
          const maxW = 100 - eff.x;
          return { ...it, w: Math.min(maxW, rawW) };
        }),
      );
    } else if (drag.kind === "text") {
      setDraftText((prev) => {
        if (!prev) return prev;
        if (drag.mode === "move") {
          const rawX = pxToX(xToPx(drag.origX) + dxPx);
          const rawY = pxToY(yToPx(drag.origY) + dyPx);
          return {
            ...prev,
            x: clampX(rawX, prev.w),
            y: clampY(rawY, prev.w, "text", null),
          };
        }
        const rawW = pxToW(wToPx(drag.origW) + dxPx);
        const maxW = 100 - prev.x;
        return { ...prev, w: Math.min(maxW, rawW) };
      });
    }
  };

  const onCanvasUp = () => setDrag(null);

  const apply = () => {
    onChange(draftItems, draftText);
    onClose();
  };

  const reset = () => {
    // Place description below the last auto-layout image instead of keeping
    // the original y, which can overlap images on wide/landscape viewports.
    const maxBottom = auto.reduce((max, pos, i) => {
      const ratio = ratiosList[i] ?? aspectRatio;
      return Math.max(max, pos.y + pos.w * actualVwToVh * ratio);
    }, 0);
    setDraftItems(items.map((it) => ({ ...it, x: undefined, y: undefined, w: undefined })));
    if (draftText) setDraftText({ ...draftText, y: Math.ceil(maxBottom + 12) });
  };

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBody}>
        <header style={modalHeader}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 500 }}>{title}</h3>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </header>
        <p style={modalHint}>
          Drag a tile to reposition. Drag the bottom-right corner to resize. The light tile in the middle is the description block (work-detail only).
        </p>
        <div
          ref={canvasRef}
          onMouseMove={onCanvasMove}
          onMouseUp={onCanvasUp}
          onMouseLeave={onCanvasUp}
          style={{
            position: "relative",
            width: CANVAS_W,
            height: CANVAS_H,
            margin: "0 auto",
            background: "#0e0e0e",
            border: "1px solid #1a1a1a",
            borderRadius: 4,
            overflow: "hidden",
            userSelect: "none",
          }}
        >
          {draftItems.map((item, i) => {
            const eff = effectiveItem(item, i);
            const left = xToPx(eff.x);
            const top = yToPx(eff.y);
            const width = wToPx(eff.w);
            const height = cardHPx(eff.w, item.url);
            const isDragging = drag && drag.kind === "item" && drag.index === i;
            return (
              <div
                key={item.id}
                onMouseDown={onMouseDownItem(i, "move")}
                title={item.label}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  background: "rgba(244,236,224,0.85)",
                  border: isDragging ? "2px solid #d49b7c" : "1px solid #2a2a2a",
                  borderRadius: 2,
                  cursor: drag && drag.mode === "move" ? "grabbing" : "grab",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  zIndex: isDragging ? 50 : 10,
                }}
              >
                {item.url ? (
                  <img
                    src={item.url}
                    alt=""
                    draggable={false}
                    style={{ width: "100%", flex: 1, objectFit: "cover", pointerEvents: "none" }}
                  />
                ) : (
                  <div style={{ flex: 1, background: "#222" }} />
                )}
                <div
                  style={{
                    padding: "2px 4px",
                    background: "rgba(12,12,12,0.85)",
                    color: "#f4ece0",
                    fontSize: 9,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  #{i + 1} · {item.label}
                </div>
                {/* Resize handle, bottom-right */}
                <div
                  onMouseDown={onMouseDownItem(i, "resize")}
                  style={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    width: 12,
                    height: 12,
                    background: "rgba(212,155,124,0.95)",
                    cursor: "nwse-resize",
                    borderTopLeftRadius: 2,
                  }}
                />
              </div>
            );
          })}

          {draftText && (
            <div
              onMouseDown={onMouseDownText("move")}
              title={draftText.label}
              style={{
                position: "absolute",
                left: xToPx(draftText.x),
                top: yToPx(draftText.y),
                width: wToPx(draftText.w),
                height: textHPx(draftText.w),
                background: "rgba(140, 90, 200, 0.55)",
                border: drag && drag.kind === "text" ? "2px solid #c8a8ff" : "1px dashed #b894e8",
                borderRadius: 2,
                cursor: drag && drag.mode === "move" ? "grabbing" : "grab",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 9,
                color: "#f4ece0",
                textAlign: "center",
                padding: 4,
                zIndex: drag && drag.kind === "text" ? 60 : 5,
              }}
            >
              {draftText.label}
              <div
                onMouseDown={onMouseDownText("resize")}
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 12,
                  height: 12,
                  background: "rgba(212,155,124,0.95)",
                  cursor: "nwse-resize",
                  borderTopLeftRadius: 2,
                }}
              />
            </div>
          )}
        </div>
        <footer style={modalFooter}>
          <button onClick={reset} style={ghostBtn}>reset to auto-layout</button>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button onClick={onClose} style={ghostBtn}>cancel</button>
            <button onClick={apply} style={primaryBtn}>apply</button>
          </div>
        </footer>
      </div>
    </div>
  );
}

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  display: "grid",
  placeItems: "center",
  zIndex: 200,
  padding: "2rem",
};

const modalBody = {
  background: "#0c0c0c",
  border: "1px solid #2a2a2a",
  borderRadius: 6,
  width: "min(540px, 100%)",
  maxHeight: "92vh",
  overflow: "auto",
  padding: "1.4rem",
  color: "#f4ece0",
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0.6rem",
};

const modalHint = { color: "#888", fontSize: "0.78rem", margin: 0, marginBottom: "0.8rem" };

const modalFooter = {
  marginTop: "1rem",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.6rem",
};

const ghostBtn = {
  background: "transparent",
  color: "#aaa",
  border: "1px solid #2a2a2a",
  padding: "0.45rem 0.9rem",
  fontSize: "0.85rem",
  borderRadius: 3,
  cursor: "pointer",
};

const primaryBtn = {
  background: "#f4ece0",
  color: "#0c0c0c",
  border: "none",
  padding: "0.45rem 0.9rem",
  fontSize: "0.85rem",
  borderRadius: 3,
  cursor: "pointer",
  fontWeight: 500,
  letterSpacing: "0.04em",
};

const closeBtn = {
  background: "transparent",
  color: "#888",
  border: "none",
  fontSize: "1rem",
  cursor: "pointer",
};
